import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { hideModal } from "actions/modal";
import { Modal } from "@/components/modal/modal";
import styled from "styled-components";
import { Button, FormSize } from "ui/ecosystems/forms";
import { User } from "features/user";
import { getUser } from "selectors/commonSelectors";
import { hasExternalApprovals } from "utils/feature-gates";
import { deleteSearchParamWhenCloseModal } from "libs/modals/init";
import { MODAL_NAMES } from "constants/Modals";
import { openSupportChat } from "../../libs/support";
import { goToManagePlan } from "utils/manage-plan";

const ExternalPermissionModal = (): JSX.Element => {
  const dispatch = useDispatch();
  const user: User = useSelector(getUser);

  const closeModal = () => {
    deleteSearchParamWhenCloseModal(MODAL_NAMES.EXTERNAL_REVIEW);
    dispatch(hideModal());
  };

  const activeChat = (event: React.MouseEvent) => {
    event.preventDefault();
    openSupportChat(
      "Hello, I'd like to know more about external review and collaboration."
    );
  };

  const openManagePlan = (event: React.MouseEvent) => {
    event.preventDefault();
    closeModal();
    goToManagePlan({ addon: "Approvals-advanced-addon" });
  };

  // The two display modes are: "you already have it" (Accelerate / Custom or
  // External Approvals add-on) vs "you need to upgrade" (everyone else). The
  // entitlement check is the source of truth so customers who buy the add-on
  // immediately see the included-in-your-plan view.
  const hasExternalAccess = hasExternalApprovals(user);

  let modalContent = null;
  let modalTitle = "";

  if (!hasExternalAccess) {
    modalTitle = "External Review — add the External Approvals add-on";
    modalContent = (
      <Desciption>
        <img
          src="https://schedugram-client.s3.amazonaws.com/external-review-permission-upgrade.png"
          alt="external-review-modal-image"
        />
        <h3>Work on reviews and approvals the quick and easy way.</h3>

        <p>
          Collaborate internally and externally on post creation, review and
          approval, right from your Sked dashboard.
        </p>

        <p>Get teammates on-board:</p>
        <ul>
          <li>Tag and leave internal comments</li>
          <li>Create custom workflows to streamline your process</li>
        </ul>

        <p>Loop in external collaborators (no login required):</p>
        <ul>
          <li>Send a calendar view of what's upcoming</li>
          <li>Make your comments visible for alignment</li>
        </ul>

        <p>
          Add the External Approvals add-on to your plan to unlock external
          reviewer links — no plan upgrade required.{" "}
          <a onClick={openManagePlan}>Manage your plan</a> or{" "}
          <a onClick={activeChat}>chat with us</a> if you have questions.
        </p>
      </Desciption>
    );
  }

  if (hasExternalAccess) {
    modalTitle = "External Review - Included in your plan!";
    modalContent = (
      <Desciption>
        <img
          src="https://schedugram-client.s3.amazonaws.com/external-review-permission-enable.png"
          alt="external-review-modal-image"
        />

        <h3>
          Loop in external reviewers and approvers the quick and easy way.
        </h3>

        <p>
          Collaborate with external stakeholders on reviews and approvals, right
          from your Sked dashboard. No extra logins needed.
        </p>

        <ul>
          <li>Send a calendar preview of what's upcoming</li>
          <li>Get approvals with just a click of a button</li>
          <li>Make your comments visible for alignment</li>
        </ul>

        <p>
          Maximize your collaboration on Sked.{" "}
          <a onClick={activeChat}>Chat with us</a> to get external review set up
          (included in your plan - no extra fees!).
        </p>
      </Desciption>
    );
  }

  return (
    <StyledModal
      data-testid="externalPermissionModal"
      show={true}
      onHide={closeModal}
    >
      <Modal.Header closeButton onHide={closeModal}>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modalContent}
        <Modal.Footer className="justify-end">
          {!hasExternalAccess && (
            <ModalButton
              primary
              size={FormSize.medium}
              onClick={openManagePlan}
            >
              Manage plan
            </ModalButton>
          )}
          <ModalButton
            primary={hasExternalAccess}
            size={FormSize.medium}
            onClick={activeChat}
          >
            Chat to Support
          </ModalButton>
        </Modal.Footer>
      </Modal.Body>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  width: 730px;
  max-width: 90%;
`;

const Desciption = styled.div`
  font-size: 14px;
  margin-bottom: 20px;
  margin-top: 15px;

  img {
    max-width: 100%;
  }
`;

const ModalButton = styled(Button)`
  padding: 10px 20px;
`;
export default ExternalPermissionModal;
